import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Filter, MessageSquare, Users, Gift,
  LayoutDashboard, DollarSign, TrendingUp, UserCheck, Clock, ShieldCheck,
  Activity, Sparkles, XCircle, AlertTriangle, Play, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * AdminDashboard — owner-only ops surface. Tabs: Overview / Users /
 * Referrals / Feedback. Access is gated by ADMIN_EMAILS on the backend;
 * the frontend calls `/support/whoami` before rendering anything.
 */

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "referrals", label: "Referrals", icon: Gift },
  { key: "timeline", label: "Timeline", icon: Activity },
  { key: "feedback", label: "Feedback", icon: MessageSquare },
];

export default function AdminDashboard({ initialTab = "overview" }) {
  const { agent } = useAuth();
  const [state, setState] = useState({ checking: true, isAdmin: false });
  const [tab, setTab] = useState(initialTab);
  // Segment filter set from the Overview cards; scoped to the Users tab.
  // "all" | "active" | "trialing" | "canceling" | "with_clients"
  const [segment, setSegment] = useState("all");

  const gotoSegment = (seg) => { setSegment(seg); setTab("users"); };

  useEffect(() => {
    api.get("/support/whoami")
      .then(({ data }) => setState({ checking: false, isAdmin: !!data.is_admin }))
      .catch(() => setState({ checking: false, isAdmin: false }));
  }, []);

  if (!agent) return <Navigate to="/login" replace />;
  if (state.checking) return <div className="text-slate-500 text-sm">Checking access…</div>;
  if (!state.isAdmin) {
    return (
      <div className="grid place-items-center py-20">
        <div className="max-w-sm text-center">
          <ShieldCheck className="h-10 w-10 mx-auto text-slate-300" />
          <div className="mt-3 font-heading text-2xl font-bold text-slate-900">Restricted</div>
          <p className="mt-2 text-sm text-slate-600">This dashboard is only visible to iKEY operators.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }}>
            <ArrowLeft className="h-4 w-4" /> Back to app
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Owner</div>
          <h1 className="mt-0.5 font-heading text-3xl font-bold tracking-tighter text-slate-900">Admin dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Everything happening across iKEY, in one place.</p>
        </div>
      </div>

      <div className="mt-6 border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap ${tab === key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab gotoSegment={gotoSegment} gotoTab={setTab} />}
        {tab === "users" && <UsersTab segment={segment} setSegment={setSegment} />}
        {tab === "referrals" && <ReferralsTab />}
        {tab === "timeline" && <TimelineTab />}
        {tab === "feedback" && <FeedbackTab />}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────── OVERVIEW ──

function OverviewTab({ gotoSegment, gotoTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/summary")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load metrics"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  // Each card is a button — clicking drills into the Users tab pre-filtered
  // by segment, except "Open feedback" which jumps to the Feedback tab.
  const cards = [
    { key: "total_agents",       label: "Total agents",       value: data.total_agents,          icon: Users,       tint: "bg-indigo-50 text-indigo-700", onClick: () => gotoSegment("all") },
    { key: "active_subscribers", label: "Active subscribers", value: data.active_subscribers,    icon: UserCheck,   tint: "bg-emerald-50 text-emerald-700", onClick: () => gotoSegment("active") },
    { key: "trialing_agents",    label: "Trialing",           value: data.trialing_agents,       icon: Clock,       tint: "bg-amber-50 text-amber-700",   onClick: () => gotoSegment("trialing") },
    { key: "total_clients",      label: "Total clients",      value: data.total_clients,         icon: Users,       tint: "bg-sky-50 text-sky-700",       onClick: () => gotoSegment("with_clients") },
    { key: "monthly_revenue_usd",label: "Monthly revenue",    value: `$${data.monthly_revenue_usd.toLocaleString()}`, icon: DollarSign, tint: "bg-rose-50 text-rose-700", onClick: () => gotoSegment("active") },
    { key: "open_feedback",      label: "Open feedback",      value: data.open_feedback,         icon: MessageSquare, tint: "bg-slate-100 text-slate-700", onClick: () => gotoTab("feedback") },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-overview-grid">
        {cards.map(c => (
          <button
            key={c.key}
            type="button"
            onClick={c.onClick}
            className="text-left rounded-lg border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-sm transition-all group"
            data-testid={`admin-metric-${c.key}`}
          >
            <div className={`inline-flex h-9 w-9 items-center justify-center rounded-md ${c.tint}`}>
              <c.icon className="h-4 w-4" />
            </div>
            <div className="mt-3 text-xs font-medium uppercase tracking-widest text-slate-500 flex items-center justify-between">
              <span>{c.label}</span>
              <span className="text-slate-300 group-hover:text-slate-500 text-[10px] font-semibold">VIEW →</span>
            </div>
            <div className="mt-1 font-heading text-2xl font-bold tabular-nums text-slate-900">{c.value}</div>
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-2">
        <TrendingUp className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
        <div>
          MRR is calculated as <span className="font-semibold">active subscribers × ${data.monthly_price_usd}/mo</span>.
          Trialing agents are those with an active free-trial period who haven't yet subscribed.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────── USERS ──

const SUB_COLORS = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  trialing: "bg-amber-50 text-amber-700 ring-amber-200",
  canceling: "bg-amber-50 text-amber-700 ring-amber-200",
  past_due: "bg-rose-50 text-rose-700 ring-rose-200",
  canceled: "bg-slate-100 text-slate-500 ring-slate-200",
  inactive: "bg-slate-100 text-slate-500 ring-slate-200",
};

const USER_SEGMENTS = [
  { key: "all",          label: "All" },
  { key: "active",       label: "Active subscribers" },
  { key: "trialing",     label: "Trialing" },
  { key: "canceling",    label: "Canceling" },
  { key: "with_clients", label: "With clients" },
];

// "View as Agent" button — mints an impersonation JWT and opens a new tab
// that boots the agent's session via URL fragment + sessionStorage.
// sessionStorage keeps the impersonation scoped to the new tab so the
// admin's original tab keeps its real Supabase session intact.
function ImpersonateButton({ user }) {
  const [pending, setPending] = useState(false);
  const impersonate = async () => {
    setPending(true);
    try {
      const { data } = await api.post(`/admin/agents/${user.id}/impersonate`);
      const bootstrap = encodeURIComponent(JSON.stringify({
        token: data.token,
        expires_at: data.expires_at,
        target: data.target,
        actor: data.actor,
      }));
      // Fragment (`#imp=`) is not sent to the server or logged in access
      // logs the way a query string is. New tab boots into Run My Day.
      const url = `${window.location.origin}/#imp=${bootstrap}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start impersonation");
    } finally {
      setPending(false);
    }
  };
  return (
    <button
      type="button"
      onClick={impersonate}
      disabled={pending}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      title={`Open a new tab logged in as ${user.full_name}`}
      data-testid={`admin-impersonate-${user.id}`}
    >
      {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
      View as agent
    </button>
  );
}



function UsersTab({ segment = "all", setSegment }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/users")
      .then(r => setRows(r.data))
      .catch(() => toast.error("Could not load users"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => {
    // Segment gate first, then free-text search.
    if (segment === "active" && r.subscription_status !== "active") return false;
    if (segment === "trialing") {
      const trialActive = r.trial_end_at && new Date(r.trial_end_at) > new Date();
      if (!(trialActive && r.subscription_status !== "active")) return false;
    }
    if (segment === "canceling" && r.subscription_status !== "canceling") return false;
    if (segment === "with_clients" && (r.client_count || 0) === 0) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.email || "").toLowerCase().includes(s) || (r.full_name || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Segment</span>
        {USER_SEGMENTS.map(s => (
          <button
            key={s.key}
            onClick={() => setSegment?.(s.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${segment === s.key ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"}`}
            data-testid={`admin-users-segment-${s.key}`}
          >
            {s.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500 tabular-nums" data-testid="admin-users-count">
          {filtered.length} shown
        </span>
      </div>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="flex-1 min-w-[240px] rounded-md border border-slate-300 px-3 py-2 text-sm"
          data-testid="admin-users-search"
        />
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="admin-users-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Agent</th>
                <th className="text-left px-4 py-3">Subscription</th>
                <th className="text-right px-4 py-3">Clients</th>
                <th className="text-left px-4 py-3">Signed up</th>
                <th className="text-left px-4 py-3">Renews / Ends</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No matches.</td></tr>
              )}
              {filtered.map(u => (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{u.full_name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ring-inset ${SUB_COLORS[u.subscription_status] || SUB_COLORS.inactive}`}>
                      {u.subscription_status}
                    </span>
                    {!u.is_active && <span className="ml-2 text-[10px] font-semibold text-slate-500">deactivated</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{u.client_count}</td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">
                    {u.subscription_current_period_end
                      ? new Date(u.subscription_current_period_end).toLocaleDateString()
                      : u.trial_end_at
                        ? <span className="text-slate-400">trial · {new Date(u.trial_end_at).toLocaleDateString()}</span>
                        : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ImpersonateButton user={u} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── REFERRALS ──

function ReferralsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/referrals")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load referrals"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  const totals = [
    { label: "Referrers", value: data.totals.total_referrers },
    { label: "Total referred", value: data.totals.total_referred },
    { label: "Subscribed", value: data.totals.total_subscribed },
    { label: "Free months granted", value: data.totals.total_free_months_granted },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-referrals-totals">
        {totals.map(t => (
          <div key={t.label} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">{t.label}</div>
            <div className="mt-1 font-heading text-2xl font-bold tabular-nums text-slate-900">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-referrals-table">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Referrer</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-right px-4 py-3">Referred</th>
                <th className="text-right px-4 py-3">Subscribed</th>
                <th className="text-right px-4 py-3">Free months</th>
              </tr>
            </thead>
            <tbody>
              {data.leaderboard.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No referral codes minted yet.</td></tr>
              )}
              {data.leaderboard.map(r => (
                <tr key={r.agent_id} className="border-t border-slate-100" data-testid={`admin-ref-row-${r.agent_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{r.full_name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.referral_code}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">{r.referred_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">{r.subscribed_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">{r.free_months_credit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout timeline — one row per referral reward we've actually
          granted (via the Stripe webhook). Lets ops reconcile the
          leaderboard totals against real events + verify vanity slugs
          are attributing correctly. */}
      <div className="mt-6 rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="admin-referral-payouts">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-heading text-base font-bold tracking-tight text-slate-900">Recent payouts</div>
            <div className="text-xs text-slate-500">Every free-month grant, newest first — auto-logged when a referred agent's first Stripe payment clears.</div>
          </div>
          <div className="text-xs text-slate-500 tabular-nums">
            {(data.recent_payouts || []).length} logged
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Referrer</th>
                <th className="text-left px-4 py-3">Referred agent</th>
                <th className="text-left px-4 py-3">New period end</th>
              </tr>
            </thead>
            <tbody>
              {(!data.recent_payouts || data.recent_payouts.length === 0) && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">No referral payouts yet.</td></tr>
              )}
              {(data.recent_payouts || []).map(p => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-payout-row-${p.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-500 tabular-nums whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900">{p.referrer_email}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.metadata?.referral_code || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {p.metadata?.referred_agent_email || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-emerald-700 tabular-nums whitespace-nowrap">
                    {p.metadata?.new_period_end ? new Date(p.metadata.new_period_end).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────── FEEDBACK ──

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "archived", label: "Archived" },
];

const CATEGORY_COLORS = {
  bug: "bg-rose-50 text-rose-700 ring-rose-200",
  feature: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  billing: "bg-amber-50 text-amber-700 ring-amber-200",
  general: "bg-slate-100 text-slate-700 ring-slate-200",
};
const STATUS_COLORS = {
  new: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  in_progress: "bg-amber-50 text-amber-700 ring-amber-200",
  resolved: "bg-slate-100 text-slate-600 ring-slate-200",
  archived: "bg-slate-50 text-slate-400 ring-slate-200",
};

function FeedbackTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/support/feedback?status_filter=${statusFilter}` : "/support/feedback";
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load feedback");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    const prev = rows;
    setRows(rs => rs.map(r => r.id === id ? { ...r, status: newStatus } : r));
    try {
      await api.patch(`/support/feedback/${id}`, { status: newStatus });
      toast.success("Updated");
    } catch (e) {
      setRows(prev);
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Filter className="h-3.5 w-3.5" /> Filter status</span>
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${statusFilter === opt.value ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"}`}
            data-testid={`admin-fb-filter-${opt.value || "all"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading && rows.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="admin-fb-empty">
          <MessageSquare className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No feedback here</p>
          <p className="text-sm text-slate-500 mt-1">Nothing matches this filter yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-fb-list">
          {rows.map(r => (
            <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid={`admin-fb-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ring-inset ${CATEGORY_COLORS[r.category] || CATEGORY_COLORS.general}`}>
                      {r.category}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ring-inset ${STATUS_COLORS[r.status] || STATUS_COLORS.new}`}>
                      {r.status.replace("_", " ")}
                    </span>
                    <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{r.body}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">{r.agent.full_name}</span>
                    <a href={`mailto:${r.agent.email}`} className="hover:text-slate-900">{r.agent.email}</a>
                    {r.page_path && <span className="font-mono text-slate-400">from {r.page_path}</span>}
                  </div>
                </div>
                <select
                  value={r.status}
                  onChange={e => updateStatus(r.id, e.target.value)}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                  data-testid={`admin-fb-status-${r.id}`}
                >
                  <option value="new">New</option>
                  <option value="in_progress">In progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────── TIMELINE ──
// Every subscription-related Stripe webhook we've captured, newest first.
// Colored dots per event type make "who signed up / who churned / whose
// card just bounced" scannable at a glance.

const EVENT_META = {
  trial_started:         { label: "Trial started",     dot: "bg-sky-500",     Icon: Sparkles },
  trial_ending:          { label: "Trial ending",      dot: "bg-amber-500",   Icon: Clock },
  subscription_created:  { label: "Subscribed",        dot: "bg-emerald-500", Icon: DollarSign },
  subscription_updated:  { label: "Updated",           dot: "bg-slate-400",   Icon: RefreshCw },
  subscription_cancelled:{ label: "Cancelled",         dot: "bg-rose-500",    Icon: XCircle },
  payment_failed:        { label: "Payment failed",    dot: "bg-red-600",     Icon: AlertTriangle },
};

function TimelineTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/subscription-events?event_type=${filter}` : "/admin/subscription-events";
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load timeline");
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => { acc[r.event_type] = (acc[r.event_type] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-timeline-filters">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mr-1">Filter</span>
        <button
          onClick={() => setFilter("")}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${filter === "" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}
          data-testid="admin-timeline-filter-all"
        >
          All <span className={`text-[10px] rounded-full px-1.5 ${filter === "" ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{rows.length}</span>
        </button>
        {Object.entries(EVENT_META).map(([key, m]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${filter === key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}
            data-testid={`admin-timeline-filter-${key}`}
          >
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${m.dot}`} /> {m.label}
            {totals[key] !== undefined && (
              <span className={`text-[10px] rounded-full px-1.5 ${filter === key ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{totals[key]}</span>
            )}
          </button>
        ))}
        <button
          onClick={() => load()}
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
          data-testid="admin-timeline-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading timeline…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500" data-testid="admin-timeline-empty">
          No subscription events yet. New signups, cancellations, and payment failures will land here.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4" data-testid="admin-timeline-list">
          {rows.map((r) => {
            const m = EVENT_META[r.event_type] || { label: r.event_type, dot: "bg-slate-400", Icon: Activity };
            const Icon = m.Icon;
            const when = new Date(r.created_at);
            return (
              <li key={r.id} className="pl-6 relative" data-testid={`admin-timeline-row-${r.id}`}>
                <span className={`absolute -left-[9px] top-2 h-4 w-4 rounded-full ring-2 ring-white ${m.dot} flex items-center justify-center`}>
                  <Icon className="h-2.5 w-2.5 text-white" />
                </span>
                <div className="rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{m.label}</div>
                    <time className="text-[11px] text-slate-500 tabular-nums" dateTime={r.created_at}>
                      {when.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </time>
                  </div>
                  <div className="mt-0.5 text-sm text-slate-800">{r.message}</div>
                  {r.metadata && (
                    <div className="mt-1.5 text-[11px] text-slate-500 font-mono truncate">
                      {Object.entries(r.metadata).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

